import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  ArrowLeft,
  Activity,
  Shield,
  MapPin,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Key,
  LogOut,
  RefreshCw
} from 'lucide-react';
import { auditService } from '../api/services/auditService';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { DashboardLayout } from '../components/layout/DashboardLayout';

const UserActivity: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['userActivity', userId, days],
    queryFn: () => auditService.getUserActivity(userId!, days),
    enabled: !!userId
  });

  const getActivityIcon = (eventType: string) => {
    switch (eventType) {
      case 'LOGIN_SUCCESS':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'LOGIN_FAILURE':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'LOGOUT':
        return <LogOut className="w-4 h-4 text-blue-500" />;
      case 'PASSWORD_CHANGE':
      case 'PASSWORD_RESET':
        return <Key className="w-4 h-4 text-purple-500" />;
      case 'ACCOUNT_LOCKED':
      case 'ACCOUNT_BANNED':
        return <Shield className="w-4 h-4 text-orange-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getLocationString = (location?: { city?: string; region?: string; country?: string }) => {
    if (!location) return 'Unknown location';
    const parts = [];
    if (location.city) parts.push(location.city);
    if (location.region) parts.push(location.region);
    if (location.country) parts.push(location.country);
    return parts.join(', ') || 'Unknown location';
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6">
          <Card className="border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                <span>Failed to load user activity</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/audit-logs')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Audit Logs
          </Button>
          <div>
            <h1 className="text-2xl font-bold">User Activity</h1>
            <p className="text-gray-600">Activity history for user {userId}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => refetch()}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Logins</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {data.summary.total_logins}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Failed Attempts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {data.summary.failed_logins}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Unique Locations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {data.summary.unique_locations}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Risk Events</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {data.summary.risk_events}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Security Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Password Changes</p>
              <p className="text-lg font-semibold">{data.summary.password_changes}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Password Resets</p>
              <p className="text-lg font-semibold">{data.summary.password_resets}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Unique IPs</p>
              <p className="text-lg font-semibold">{data.summary.unique_ips}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Success Rate</p>
              <p className="text-lg font-semibold">
                {data.summary.total_logins + data.summary.failed_logins > 0
                  ? Math.round((data.summary.total_logins / (data.summary.total_logins + data.summary.failed_logins)) * 100)
                  : 0}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Timeline</CardTitle>
          <CardDescription>
            Showing {data.activities.length} activities in the last {days} days
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <div className="space-y-3">
              {data.activities.map((activity, idx) => (
                <div 
                  key={idx}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getActivityIcon(activity.event_type)}
                        <span className="font-medium">
                          {auditService.getEventTypeLabel(activity.event_type)}
                        </span>
                        {activity.success ? (
                          <Badge className="bg-green-100 text-green-800">Success</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">Failed</Badge>
                        )}
                        {activity.risk_score && activity.risk_score > 50 && (
                          <Badge className="bg-orange-100 text-orange-800">
                            Risk: {activity.risk_score}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {auditService.formatTimestamp(activity.timestamp)}
                        </div>
                        {activity.geo_location && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {getLocationString(activity.geo_location)}
                          </div>
                        )}
                        {activity.failure_reason && (
                          <div className="col-span-2">
                            <span className="font-medium">Reason: </span>
                            {auditService.getFailureReasonLabel(activity.failure_reason)}
                          </div>
                        )}
                      </div>
                      
                      {activity.suspicious_indicators && activity.suspicious_indicators.length > 0 && (
                        <div className="mt-2 flex gap-2">
                          {activity.suspicious_indicators.map((indicator, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {indicator}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {activity.response_time_ms && (
                      <div className="text-sm text-gray-500">
                        {activity.response_time_ms}ms
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {data.activities.length === 0 && (
                <div className="text-center py-8 text-gray-600">
                  No activity found in the selected period
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
};

export default UserActivity;